import { TAddress } from 'router/types'
import { WEB_STORAGE } from 'constants/common'
import { notification } from 'components/Notification'
import { TOption } from 'components/Select/types'
import { ROLES } from 'features/Permission'
import { VALIDATION_MESSAGES } from 'constants/txt'
import { TDocumentsErrors } from '../constants'
import { IVendorInfoErrors } from '../redux/store/vendorInfo/types'
import { TUserForm } from '../components/Forms/forms.user.d'
import { Avatar } from '../features/UserProfile/types'
import { routes } from '../router/Config/config.routes'
import { COUNTRIES_LIST } from 'features/MyCommunity/Vendors/VendorCreation/constants'
import { IUser } from '../redux/store/user/types'

export const websiteValidationRegex = new RegExp(
  /^(https?:\/\/)?((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i
)

export const toCapitalizeChart = (str: string) => {
  const strArr = str.replace(/_/g, ' ').split('')
  strArr[0] = strArr[0].toUpperCase()
  return strArr.join('')
}

export const getCapitalCharName = (first_name: string, last_name: string) =>
  `${first_name?.charAt(0) || ''}${last_name?.charAt(0) || ''}`.toUpperCase()

export const cleanUpPrivateStorage = () => {
  Object.values(WEB_STORAGE).forEach((item) => {
    if (
      item === WEB_STORAGE.LAST_PAGE ||
      item === WEB_STORAGE.HIDE_APP_VERSION
    ) {
      return
    }
    localStorage.removeItem(item)
    sessionStorage.removeItem(item)
  })
}

export const isTableHasParams = <T>(searchValue: string, filters: T): boolean =>
  !!searchValue || !!Object.values(filters).length

export const getHideAppVersionParam = () =>
  localStorage.getItem(WEB_STORAGE.HIDE_APP_VERSION)
export const getLocalAccessToken = () =>
  localStorage.getItem(WEB_STORAGE.ACCESS_TOKEN)
export const setLocalAccessToken = (accessToken: string) =>
  localStorage.setItem(WEB_STORAGE.ACCESS_TOKEN, accessToken)
export const getLocalRefreshToken = () =>
  localStorage.getItem(WEB_STORAGE.REFRESH_TOKEN)
export const setLocalRefreshToken = (refreshToken: string) =>
  localStorage.setItem(WEB_STORAGE.REFRESH_TOKEN, refreshToken)
export const setLocalEmail = (email: string) =>
  localStorage.setItem(WEB_STORAGE.EMAIL, email)
export const getLocalEmail = () => localStorage.getItem(WEB_STORAGE.EMAIL)
export const setLocalPass = (pass: string) =>
  localStorage.setItem(WEB_STORAGE.PASSWORD, pass)
export const getLocalPass = () => localStorage.getItem(WEB_STORAGE.PASSWORD)
export const setLocalPhoneNumber = (phone: string) =>
  localStorage.setItem(WEB_STORAGE.PHONE_NUMBER, phone)
export const getLocalPhoneNumber = () =>
  localStorage.getItem(WEB_STORAGE.PHONE_NUMBER)
export const setLocalConfirmToken = (token: string) =>
  localStorage.setItem(WEB_STORAGE.LOCAL_TOKEN, token)
export const getLocalConfirmToken = () =>
  localStorage.getItem(WEB_STORAGE.LOCAL_TOKEN)
export const setLocalLastPage = (page: string) =>
  localStorage.setItem(WEB_STORAGE.LAST_PAGE, page)
export const getLocalLastPage = () =>
  localStorage.getItem(WEB_STORAGE.LAST_PAGE)
export const setLocalCalendarView = (page: string) =>
  localStorage.setItem(WEB_STORAGE.CALENDAR_VIEW, page)
export const getLocalCalendarView = () =>
  localStorage.getItem(WEB_STORAGE.CALENDAR_VIEW)

export const formatPhoneNumber = (phoneNumber?: string | number) =>
  phoneNumber && String(phoneNumber).length >= 0
    ? String(phoneNumber)
        .replace('+1', '')
        .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2 $3')
    : String(phoneNumber).replace('+1', '')

export const fullFormatPhoneNumber = (phoneNumber?: string) =>
  phoneNumber
    ? phoneNumber
        .toString()
        .replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1 ($2) $3 $4')
    : phoneNumber || ''

export const parserPhoneNumber = (phoneNumber?: string) =>
  phoneNumber?.toString().replace(/\D+/g, '').length !== 10

export const formatPhoneNumberToRequest = (phone_number?: string) =>
  phone_number
    ? !phone_number.toString().includes('+1')
      ? '+1' + phone_number
      : phone_number
    : null

export const formatMoney = (value) =>
  `${Intl.NumberFormat('en-US').format(
    value.replace(/([^\d]*)(\d*(\.\d{0,2})?)(.*)/, '$2')
  )}`

export const formatAmount = (value: string | number) =>
  `$${String(Number(value).toFixed(2)).replace(/\B(?=(\d{3})+(?!\d).?)/g, ',')}`

export const formatName = (value?: string) =>
  value
    ? value.replace(/[^a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/? ]/g, '')
    : ''

export const formatCanadianPostalCode = (value?: string) => {
  if (!value) return value

  const chars = value
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 6)
    .toUpperCase()

  if (chars.length <= 3) return chars
  return `${chars.slice(0, 3)} ${chars.slice(3)}`
}

export const formatAddress = (address?: TAddress) => {
  if (
    !address?.street ||
    !address?.city ||
    !address?.state ||
    !address?.zip_code
  ) {
    return ''
  }

  const countryLabel = COUNTRIES_LIST.find(
    (country) => country.value === address.country
  )?.label

  return `${address.street}
    ${address.city}, ${address.state} ${address.zip_code}
    ${countryLabel ? `${countryLabel}` : ''}`
}

export const validateMessages = {
  default: 'Validation error on field ${label}',
  required: '${label} is required',
  whitespace: '${label} cannot be empty',
  types: {
    email: 'Enter a valid ${name}'
  },
  pattern: {
    mismatch: 'Enter a valid ${label}'
  }
}

export const isEqualObjects = (object1, object2) => {
  const props1 = Object.getOwnPropertyNames(object1)
  const props2 = Object.getOwnPropertyNames(object2)

  if (props1.length !== props2.length) {
    return false
  }

  for (let i = 0; i < props1.length; i += 1) {
    const prop = props1[i]
    const bothAreObjects =
      typeof object1[prop] === 'object' && typeof object2[prop] === 'object'

    if (
      (!bothAreObjects && object1[prop] !== object2[prop]) ||
      (bothAreObjects && !isEqualObjects(object1[prop], object2[prop]))
    ) {
      return false
    }
  }
  return true
}

export const validateFileTypes = (types, file, maxFileSize = 5242880) => {
  let message = ''
  if (
    !Object.values(types)
      .flat()
      .find((i) => file.type === i)
  ) {
    message = VALIDATION_MESSAGES.SM0038
  }
  if (file.size > maxFileSize) {
    const maxSizeMB = Math.round(maxFileSize / (1024 * 1024))
    message = `Max file size is ${maxSizeMB} MB.`
  }
  if (message) {
    notification.error({ message })
  }
  return null
}

export const validateOnCondition = (
  condition: boolean,
  errorMessage?: string
): Promise<void> => {
  return new Promise<void>((res, rej) =>
    condition ? rej(errorMessage) : res()
  )
}

export const sortAscByKey =
  <T>(key: keyof T, reverse = false) =>
  (a, b) =>
    reverse ? b[key] - a[key] : a[key] - b[key]

export const fileNameFormatter = (file) => {
  const res = decodeURI(file?.split('/')[5]?.split('?')[0])
  if (res !== 'undefined') {
    return res
  } else {
    return file
  }
}

export const returnSelectOptions = (
  arr: Array<object>,
  settings: { labelKey?: string; valueKey?: string } = {}
): TOption[] => {
  const { labelKey = 'name', valueKey = 'uuid' } = settings

  if (arr?.length) {
    return arr?.map((item) => ({
      label: item[labelKey],
      value: item[valueKey]
    }))
  }

  return []
}

export const safeString = (value?: string | null) =>
  value ? String(value) : ''

export const stringOrUndefined = (value?: string | null) =>
  value ? String(value) : undefined

export const combineStrings = (
  primary: string,
  secondary?: string,
  separator = ' at '
): string => `${primary}${secondary ? `${separator}${secondary}` : ''}`

export const createKeyFromObject = (values: Record<string, unknown>): string =>
  Object.values(values)
    .map((value) => {
      if (value instanceof File) return value.name
      if (value != null && typeof value === 'object' && 'valueOf' in value) {
        const primitive = (value as { valueOf: () => unknown }).valueOf()
        if (typeof primitive !== 'object') return String(primitive)
      }
      return String(value ?? '')
    })
    .join('-')

export const safeNumber = (value?: string | number | null) =>
  parseFloat(String(value)) || 0

export const formatNumberUS = (
  value?: string | number | null,
  options?: Intl.NumberFormatOptions
) => safeNumber(value).toLocaleString('en-US', options ?? {})

export const safeCurrency = (
  value?: string | number | null,
  options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'USD'
  }
) => safeNumber(value).toLocaleString('en-US', options)

export const convertStringToCompareFormat = (value: string) =>
  value.trim().toLowerCase()

export const getNameInitials = (fullName: string) => {
  const splitName = fullName.split(' ')
  return `${splitName[0].charAt(0)}${splitName.pop()?.charAt(0)}`.toUpperCase()
}

export const getObjValueByQuery = (
  object: IVendorInfoErrors | null,
  query: string[]
): unknown => query.reduce((obj, prop) => obj?.[prop], object)

export const preventInitialToCorrectFormat = (
  data?: TUserForm & { avatar_logo?: Avatar }
) => ({
  ...data,
  phone_number: data?.phone_number?.replace('+1', ''),
  timezone_id: data?.timezone?.uuid || '',
  avatar: data?.avatar_logo?.uuid || data?.avatar
})

export const canSeeMyContracts = (role: string) =>
  role === ROLES.ANALYST ||
  role === ROLES.FINANCE ||
  role === ROLES.CONTRACT_STEWARD ||
  role === ROLES.CLINICAL_COORDINATOR ||
  role === ROLES.VICE_PRESIDENT ||
  role === ROLES.COMMONS_ANALYST ||
  role === ROLES.COMMONS_CONTRACT_STEWARD ||
  role === ROLES.COMMONS_CLINICAL_COORDINATOR ||
  role === ROLES.COMMONS_VICE_PRESIDENT ||
  role === ROLES.COMMUNITY_PRESIDENT

const CONTRACT_ROUTE_SEGMENTS = new Set([
  'my_contracts',
  'central_repository',
  'cognus_repository',
  'contract_pipeline',
  'opportunity_analysis',
  'contract_categories',
  'bulk_contract_creation',
  'create'
])

export const CONTRACT_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isContractUuid = (segment?: string): segment is string =>
  Boolean(
    segment &&
      !CONTRACT_ROUTE_SEGMENTS.has(segment) &&
      CONTRACT_UUID_REGEX.test(segment)
  )

export const buildContractDetailsPath = (
  role: string,
  contractId: string,
  tab?: string,
  subtab?: string
) => {
  const base = `${routes.contracts}${
    canSeeMyContracts(role) ? routes.myContracts : ''
  }/${contractId}`

  if (!tab) {
    return base
  }

  return subtab ? `${base}/${tab}/${subtab}` : `${base}/${tab}`
}

export const isUserCanSeeTheSelectionOfTheHSAndCommunityType = (user) =>
  // TODO: AT LEAST 1 category for which Community-level flag is TRUE check
  (user.role === ROLES.CONTRACT_STEWARD ||
    user.role === ROLES.VICE_PRESIDENT) &&
  user.community_level

/**
 * Returns the community-equivalent organisation ID for the current user.
 *
 * `outside_community` users belong to a health system that is not attached to
 * a community. In that context `user.health_system` plays the same structural
 * role that `user.community` plays for regular community users, so we fall back
 * to it instead.
 */
export const getUserCommunity = (user: IUser): string | undefined =>
  user.outside_community ? user.health_system : user.community

export const formatFirstLastName = ({
  firstName,
  lastName
}: {
  firstName?: string
  lastName?: string
}) => `${firstName?.charAt(0)}${lastName?.charAt(0)}`.toUpperCase()

export const handleFilesErrors = (e: TDocumentsErrors) => {
  const { data } = e
  const { field_errors, details, non_field_errors, file } = data ?? {}

  details?.map((err) => notification.error({ message: err }))
  non_field_errors?.map((err) => notification.error({ message: err }))
  file?.map((err) => notification.error({ message: err }))

  if (!!field_errors) {
    Object.keys(field_errors).map((key) =>
      notification.error({ message: `${field_errors[key]} : ${key}` })
    )
  }
}

export const downloadFile = (url: string) => {
  const link = document.createElement('a')
  link.href = url
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const numberIsGreaterThan0Validator = () => ({
  validator(_, value) {
    if (value === 0)
      return Promise.reject(new Error('Value must be greater than 0'))
    return Promise.resolve()
  }
})

export const getFullName = (first_name: string, last_name: string) =>
  `${first_name ?? ''} ${last_name ?? ''}`.trim()
